import * as Sentry from '@sentry/react';
import {DEFAULT_LINK_EXPIRY_DURATION} from '@sollapay/enums';
import {parseMoney} from '@sollapay/utils';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {config} from '@/app/app.config';
import {
  useAssetPicker,
  useCreatePaymentInstructionMutation,
  usePaymentInitiationMethod,
  usePaymentTransferType,
  usePaymentsQuery
} from '@/features/buyers/hooks';
import {
  computeRemainingAmountNis,
  formatPurchaseAssetNames,
  isPaymentNextDisabled,
  isPaymentRequestDirty,
  toBuyerOption
} from '@/features/buyers/utils';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {BuyersModal} from './BuyersModal';
import {PaymentRequestTabContent} from './BuyersModal/payment-request/PaymentRequestTabContent';
import {PaymentSentModal} from './PaymentSentModal';

import type {LinkExpiryDuration} from '@sollapay/enums';
import type {BuyerDTO, BuyerPurchaseDTO, PaymentInstructionDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface InitiatePaymentFlowProps {
  trustAccountId: string;
  isOpen: boolean;
  onClose: () => void;
  /** All payment instructions for this trust account. Optional — fetched here when omitted. */
  payments?: PaymentInstructionDTO[];
  /** All buyers sharing the same purchase — shows a select when more than one */
  buyers?: BuyerDTO[];
  /** Purchase (asset) to request payment for. Omit both to let the user pick one. */
  selectedPurchase?: BuyerPurchaseDTO;
}

type Step = 'payment-request' | 'payment-sent';

export const InitiatePaymentFlow: FC<InitiatePaymentFlowProps> = ({
  trustAccountId,
  isOpen,
  onClose,
  payments: fixedPayments,
  selectedPurchase: fixedPurchase,
  buyers: fixedBuyers
}) => {
  const {t} = useTranslation('trustAccounts');
  const strategy = useTrustTypeStrategy();
  const [step, setStep] = useState<Step>('payment-request');
  const [expiryDuration, setExpiryDuration] = useState<LinkExpiryDuration>(
    DEFAULT_LINK_EXPIRY_DURATION
  );
  // Regenerated on each attempt to avoid idempotency key reuse on retry
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [customAmountNis, setCustomAmountNis] = useState('');
  const [selectedBuyerId, setSelectedBuyerId] = useState('');

  const isAssetPickerMode = !fixedPurchase;
  const picker = useAssetPicker(trustAccountId, isAssetPickerMode && isOpen);

  // Skipped when the caller already has the list; the disabled picker's own list would be empty.
  const {data: fetchedPayments = []} = usePaymentsQuery(
    isOpen && !fixedPayments ? trustAccountId : ''
  );

  const purchase = fixedPurchase ?? picker.selectedPurchase;
  const buyers = fixedBuyers ?? picker.buyers;
  const payments = fixedPayments ?? fetchedPayments;

  const createPaymentMutation = useCreatePaymentInstructionMutation(trustAccountId);

  const selectedBuyer = buyers.find(b => b.id === selectedBuyerId) ?? buyers[0];
  const assetLabel = purchase ? formatPurchaseAssetNames(purchase.assets, t) : undefined;
  const maxAmountNis = purchase
    ? parseMoney(purchase.purchasePriceNis).percentOf(strategy.maxCollectionPercentage).toString()
    : '0';

  const remainingAmountNis = useMemo(
    () => (purchase ? computeRemainingAmountNis(purchase, payments, maxAmountNis) : maxAmountNis),
    [purchase, payments, maxAmountNis]
  );

  const amount = config.features.allowCustomPaymentAmount ? customAmountNis : maxAmountNis;
  const {transferType, isZahavOnly, setTransferType, resetTransferType} =
    usePaymentTransferType(amount);
  const {initiationMethod, selectedInitiationMethod, setInitiationMethod, resetInitiationMethod} =
    usePaymentInitiationMethod(selectedBuyer?.partyType);

  const handleClose = useCallback(() => {
    setStep('payment-request');
    resetInitiationMethod();
    resetTransferType();
    setExpiryDuration(DEFAULT_LINK_EXPIRY_DURATION);
    setCustomAmountNis('');
    setIdempotencyKey(crypto.randomUUID());
    setSelectedBuyerId('');
    picker.reset();
    onClose();
  }, [onClose, picker, resetTransferType, resetInitiationMethod]);

  const handleAssetChange = useCallback(
    (assetId: string) => {
      picker.onAssetChange(assetId);
      setSelectedBuyerId('');
      setCustomAmountNis('');
    },
    [picker]
  );

  const handleSendPayment = useCallback(async () => {
    if (!purchase || !initiationMethod || !selectedBuyer) return;
    if (config.features.allowCustomPaymentAmount && !customAmountNis) return;

    try {
      await createPaymentMutation.mutateAsync({
        trustBuyerPurchaseId: purchase.id,
        buyerPartyId: selectedBuyer.id,
        amount,
        initiationMethod,
        transferType,
        idempotencyKey,
        expiryDuration
      });
      Sentry.metrics.count('payment.sent', 1, {attributes: {method: initiationMethod}});
      setStep('payment-sent');
    } catch {
      Sentry.metrics.count('payment.failed', 1, {attributes: {method: initiationMethod}});
      // Error is surfaced via createPaymentMutation.error → saveError prop
      setIdempotencyKey(crypto.randomUUID());
    }
  }, [
    purchase,
    initiationMethod,
    transferType,
    expiryDuration,
    selectedBuyer,
    customAmountNis,
    amount,
    idempotencyKey,
    createPaymentMutation
  ]);

  const isNextDisabled =
    (isAssetPickerMode && !purchase) ||
    isPaymentNextDisabled({
      initiationMethod,
      allowCustomAmount: config.features.allowCustomPaymentAmount,
      customAmountNis,
      remainingAmountNis,
      enforcesCollectionCap: strategy.enforcesCollectionCap
    });

  const buyerOptions = buyers.map(toBuyerOption);

  return (
    <>
      <BuyersModal
        isOpen={isOpen && step === 'payment-request'}
        onOpenChange={open => {
          if (!open) handleClose();
        }}
        isDirty={isPaymentRequestDirty({
          initiationMethod: selectedInitiationMethod,
          customAmountNis,
          expiryDuration,
          selectedBuyerId: selectedBuyer?.id ?? '',
          defaultBuyerId: buyers[0]?.id,
          selectedAssetId:
            picker.selectedAssetId === picker.defaultAssetId ? '' : picker.selectedAssetId
        })}
        backClosesModal
        activeTab="payment-request"
        isBuyerCreated={true}
        hideBuyerStep
        nextLabel={t('view.buyers.addBuyerModal.footer.sendPaymentRequest')}
        nextDisabled={isNextDisabled}
        nextIsLoading={createPaymentMutation.isPending}
        backLabel={t('view.buyers.addBuyerModal.footer.back')}
        onNextClick={handleSendPayment}
      >
        <PaymentRequestTabContent
          amount={amount}
          onAmountChange={setCustomAmountNis}
          maxAmountNis={maxAmountNis}
          remainingAmountNis={remainingAmountNis}
          assetLabel={assetLabel}
          assetOptions={isAssetPickerMode ? picker.assetOptions : undefined}
          selectedAssetId={picker.selectedAssetId}
          onAssetChange={handleAssetChange}
          buyers={buyerOptions}
          selectedBuyerId={selectedBuyer?.id ?? ''}
          onBuyerChange={setSelectedBuyerId}
          initiationMethod={initiationMethod}
          onInitiationMethodChange={setInitiationMethod}
          transferType={transferType}
          onTransferTypeChange={setTransferType}
          isZahavOnly={isZahavOnly}
          expiryDuration={expiryDuration}
          onExpiryDurationChange={setExpiryDuration}
          saveError={createPaymentMutation.error}
        />
      </BuyersModal>

      {purchase && initiationMethod && selectedBuyer && (
        <PaymentSentModal
          isOpen={step === 'payment-sent'}
          buyerName={selectedBuyer.fullName}
          assetLabel={formatPurchaseAssetNames(purchase.assets, t)}
          purchasePriceNis={purchase.purchasePriceNis}
          initiationMethod={initiationMethod}
          amount={amount}
          onClose={handleClose}
        />
      )}
    </>
  );
};
